import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { NavigationItem } from '../../../../../../../../aumico-components/src/lib';
import {
    FxRateMethod,
    IAccountMappingTemplateGroup,
    IAnnualReport,
    IFinStatements,
    IFxAccountHistorical,
    IFxAccountRef,
    IFxHistoricalArchiveYear,
    IFxTranslation,
    ItemType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { DateLocalePipe } from '@modeso/aumico-lib-core-fe';
import { I18NextService } from 'angular-i18next';
import {
    FX_ROUNDING_TOLERANCE,
    FxTranslationHelper,
    FxYear,
    IFxDecoration,
    IFxProofRowData,
    IFxRoundingYear,
} from '../../../utils/FxTranslationHelper';
import { GroupingMaskingHelper } from '../../../utils/GroupingMaskingHelper';
import { NumberUtils } from '@modeso/aumico-lib-core-fe';

export interface IFxMethodChange {
    fliPath: string;
    accountKey: string;
    year: 'currentYear' | 'previousYear';
    method: FxRateMethod;
}

/** The user booked a year's rounding difference onto an account, or took the booking back (null). */
export interface IFxRoundingAccountChange {
    year: FxYear;
    account: IFxAccountRef | null;
}

/** The open rounding dialog: one year, its outcome, and the accounts it may be booked onto. */
export interface IFxRoundingDialog {
    year: FxYear;
    header: string;
    rounding: IFxRoundingYear;
    options: { id: string; name: string }[];
    control: FormControl;
    locked: boolean;
}

/**
 * Request to open the historical editor. The year decides the scope: the previous year is a
 * single rate and nothing else, while the current year shows the whole chain (prior-year balance →
 * prior-year rate → opening → movements → closing → weighted rate). Opening the prior year must
 * not drag this year's movements into view — they are separate decisions.
 */
export interface IFxHistoricalEditRequest {
    fliPath: string;
    accountKey: string;
    year: 'currentYear' | 'previousYear';
    accountLabel: string;
    /** Functional opening (finalPY) and closing (finalCY) of the current year. */
    functionalOpening: number;
    functionalClosing: number;
    /** Only when the prior year is on HISTORICAL is its rate the account's own — and editable. */
    previousYearHistorical: boolean;
    /** Every closed year before this one, oldest first — read-only context for the user. */
    archive: IFxHistoricalArchiveYear[];
    /** The prior year's rate: the account's own when historical, otherwise the period rate. */
    previousYearRate: number | null;
    /** A carried-forward prior year is read-only. */
    previousYearLocked: boolean;
    /** Current stored state for this account, if any. */
    historical: IFxAccountHistorical | undefined;
}

/** Above this, a gap is no longer plausible as rounding and turns red (03d §5). */
const ROUNDING_TOLERANCE = FX_ROUNDING_TOLERANCE;

/** One line of the FX-difference proof — a financial line item that is not on the closing rate. */
export interface IFxProofRow {
    label: string;
    methodLabel: string;
    /** Set only when the FLI mixes methods: the ones that occur, for the quiet sub-label. */
    mixedDetail: string;
    /** Effective rate; null when the functional basis nets to zero and no rate exists. */
    rate: number | null;
    display: number;
    displayAtClosing: number;
    /** Signed by balance-sheet side: `+d` on assets, `−d` on liabilities and equity (03d §2). */
    contribution: number;
    functional: number;
    provisional: boolean;
}

/** One year's proof: its rows, what they add up to, and how that reaches the reserve. */
export interface IFxProofYear {
    header: string;
    closingRate: number | null;
    rows: IFxProofRow[];
    sumContribution: number;
    /** The Stichtag positions' rounding residue — in the reserve, invisible in the rows (03d §5). */
    rounding: number;
    reserve: number;
    /** Functional imbalance: not a currency effect, never absorbed by the reserve (03d §2.2). */
    functionalDelta: number;
    /** Any unfinished historical position below: the sums are partial and must not look settled. */
    provisional: boolean;
}

/** One control check. `difference` is shown whenever non-zero — a tolerance may never hide it. */
export interface IFxCheck {
    label: string;
    ok: boolean;
    /** Numbers, not strings: the template formats them with the locale-aware `number` pipe. */
    left: number;
    right: number;
    difference: number;
    /** Why it failed, when the figures alone would not say (03d §4.2). */
    reason: string;
    /** Not settled yet, but fixable by the user — shown orange instead of red. */
    warning?: boolean;
    /** Year-result checks only: the year whose rounding dialog the card opens. */
    roundingYear?: FxYear;
    /** A second line under the figures, e.g. where the rounding difference is booked. */
    note?: string;
    /** The card's call to action (assign / change the rounding booking). */
    action?: string;
}

// IFxDecoration now lives in utils/FxTranslationHelper.ts (REV-21512) — the notes stamping and
// this table share one decoration shape.

/**
 * The Umrechnungstabelle: every row of the balance sheet / income statement in both currencies,
 * with a per-account, per-year rate-method dropdown.
 *
 * The view model is built with EXACTLY Grouping & Masking's pipeline (its now-static methods), so
 * both screens always show the same rows and the same functional figures. Reclasses are included
 * because the `finStatements` input MUST be the bridged `getActiveFinStatementsWithReclasses$()`
 * statements (per-account openingReClass/closingReClass already materialized; see the comment in
 * buildViewModels) — `finalCY/PY = calculatedBalance + reclass` then holds on every row. This
 * component only reads; translation is a decoration pass on top.
 */
@Component({
    selector: 'aumico-fx-conversion-table',
    templateUrl: './fx-conversion-table.component.html',
    styleUrls: ['./fx-conversion-table.component.scss'],
    // The table renders hundreds of rows; everything the template binds is precomputed in the
    // decoration pass, so default change detection would only burn cycles re-evaluating it.
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FxConversionTableComponent implements OnChanges {
    @Input() annualReport: IAnnualReport | undefined;
    @Input() finStatements: IFinStatements[] | undefined;
    @Input() fxTranslation: IFxTranslation | undefined;
    @Input() previousYearLocked = false;

    @Output() methodChange = new EventEmitter<IFxMethodChange>();
    /** The user wants to edit an account's historical data — the parent opens the modal. */
    @Output() historicalEdit = new EventEmitter<IFxHistoricalEditRequest>();
    /** The user booked a year's rounding difference onto an account (or removed the booking). */
    @Output() roundingAccountChange = new EventEmitter<IFxRoundingAccountChange>();

    /** The rounding dialog of the Kontrolle tab; null while closed. */
    roundingDialog: IFxRoundingDialog | null = null;

    readonly tabBalance = 0;
    readonly tabIncome = 1;
    readonly tabKontrolle = 2;

    navigationItems: NavigationItem[] = [
        { name: 'aumico_annual_report_import_form_balance_title', active: true },
        { name: 'aumico_annual_report_import_form_income_title', active: false },
        { name: 'aumico_annual_report_fx_table_tab_kontrolle', active: false },
    ];
    selectedTab = this.tabBalance;

    balanceSheet: IAccountMappingTemplateGroup | undefined;
    incomeStatement: IAccountMappingTemplateGroup | undefined;

    functionalCurrencyName = '';
    displayCurrencyName = '';
    headerBalanceCY = '';
    headerBalancePY = '';
    headerIncomeCY = '';
    headerIncomePY = '';

    /**
     * Years whose balance sheet does not add up in FUNCTIONAL currency. Precomputed rather than a
     * getter: the banner renders on every tab and this component is OnPush.
     */
    functionalImbalances: { header: string; delta: number }[] = [];

    /** 03d: the FX-difference proof, one per year, plus the control checks. */
    proofCY: IFxProofYear | null = null;
    proofPY: IFxProofYear | null = null;
    checks: IFxCheck[] = [];
    readonly roundingTolerance = ROUNDING_TOLERANCE;

    reserveCY: number | null = null;
    reservePY: number | null = null;
    reserveProvisionalCY = false;
    reserveProvisionalPY = false;

    /** The income statement's bottom line — see computeYearResult. */
    resultLabel = '';
    resultUsdCY = 0;
    resultUsdPY = 0;
    resultChfCY: number | null = null;
    resultChfPY: number | null = null;
    resultRateCY = '';
    resultRatePY = '';
    resultProvisionalCY = false;
    resultProvisionalPY = false;

    readonly ItemType = ItemType;
    private readonly finalCY = GroupingMaskingHelper.finalCYOf;
    private readonly finalPY = GroupingMaskingHelper.finalPYOf;

    /** Option labels, kept only to detect when the dropdown lists actually need rebuilding. */
    private methodOptionLabels: Record<'currentYear' | 'previousYear', Record<string, string>> = {
        currentYear: {},
        previousYear: {},
    };
    /** hamSimpleDropDown option lists, shared by every row of a year. */
    methodOptions: Record<'currentYear' | 'previousYear', { id: FxRateMethod; name: string }[]> = {
        currentYear: [],
        previousYear: [],
    };
    /** One control per (fliPath, accountKey, year) — reused across re-decorations. */
    private methodControls = new Map<string, FormControl>();

    /**
     * Static cell labels. Resolved once instead of piping per cell — the table renders hundreds of
     * rows under OnPush.
     */
    labels = {
        openSuffix: '',
        mixed: '',
        methodClosing: '',
        methodAverage: '',
        methodHistorical: '',
        checkBalance: '',
        checkResult: '',
        reasonOpen: '',
        reasonImbalance: '',
        reasonResultFunctional: '',
        roundingAssign: '',
        roundingChange: '',
        roundingTooLarge: '',
        roundingAccountMissing: '',
        roundingCellHint: '',
        blockedHint: '',
        provisionalHint: '',
        previousYearRateMissing: '',
    };

    /** Explicit user toggles only; untouched rows fall back to the type default in isCollapsed. */
    private accordionToggles = new Map<string, boolean>();
    /** The year-result FLIs: they start on the average rate in both statements, but stay editable. */
    private yearResultFliNames = new Set<string>();
    /** FLI names beneath the template's equity group — those accounts default to HISTORICAL. */
    private equityFliNames = new Set<string>();

    constructor(
        private readonly i18next: I18NextService,
        private readonly dateLocalePipe: DateLocalePipe,
        private readonly cdr: ChangeDetectorRef,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        const structuralChange = changes['annualReport'] != null || changes['finStatements'] != null;
        if (structuralChange && this.annualReport != null && this.hasUsableStatements()) {
            this.buildViewModels();
        }
        if (this.balanceSheet != null) {
            this.computeMethodOptionLabels();
            this.decorate(this.balanceSheet, 'balance');
            this.decorate(this.incomeStatement, 'income');
            // Order matters: the checks read the year result, and the reserve reads the proof.
            this.computeYearResult();
            this.buildProof();
            this.computeReserve();
        }
    }

    // #region view model — Grouping & Masking's pipeline, step for step

    private hasUsableStatements(): boolean {
        return FxTranslationHelper.hasUsableStatements(this.finStatements);
    }

    private buildViewModels(): void {
        const fs = this.finStatements as IFinStatements[];
        // The pipeline (and the deliberate absence of applyTransactionalReclassesToViewModel) lives
        // in FxTranslationHelper.buildViewModels — one calculation for the table, the Kontrolle tab
        // and the notes (REV-21512 Thema 8).
        const { balanceSheet, incomeStatement } = FxTranslationHelper.buildViewModels(
            fs,
            this.annualReport?.groupingAndMasking,
        );
        this.balanceSheet = balanceSheet;
        this.incomeStatement = incomeStatement;

        const balanceTemplate = fs[0].templateAndAccountsMapping.template;
        this.yearResultFliNames = FxTranslationHelper.yearResultFliNamesOf(balanceTemplate);
        this.equityFliNames = FxTranslationHelper.equityFliNamesOf(balanceTemplate);

        const generalInformation = this.annualReport?.generalInformation;
        this.functionalCurrencyName = generalInformation?.currency?.name ?? '';
        this.displayCurrencyName = generalInformation?.displayCurrency?.name ?? '';
        this.headerBalanceCY = this.formatDate(generalInformation?.balanceSheetDate);
        this.headerBalancePY = this.formatDate(generalInformation?.balancePreviousYearDate);
        this.headerIncomeCY = `${this.formatDate(generalInformation?.incomeCurrentYearFromDate)} – ${this.formatDate(
            generalInformation?.incomeCurrentYearToDate,
        )}`;
        this.headerIncomePY = `${this.formatDate(generalInformation?.incomePreviousYearFromDate)} – ${this.formatDate(
            generalInformation?.incomePreviousYearToDate,
        )}`;
    }

    /** Always the app's date pipe with the active locale — never a hand-rolled format. */
    private formatDate(iso: string | null | undefined): string {
        return iso == null ? '' : `${this.dateLocalePipe.transform(iso)}`;
    }

    // #endregion

    // #region translation decoration

    private computeMethodOptionLabels(): void {
        this.labels = {
            openSuffix: this.i18next.t('aumico_annual_report_fx_table_hist_open') as string,
            blockedHint: this.i18next.t('aumico_annual_report_fx_table_hist_blocked_hint') as string,
            provisionalHint: this.i18next.t('aumico_annual_report_fx_table_hist_provisional_hint') as string,
            previousYearRateMissing: this.i18next.t('aumico_annual_report_fx_table_hist_py_rate_missing') as string,
            mixed: this.i18next.t('aumico_annual_report_fx_proof_mixed') as string,
            methodClosing: this.i18next.t('aumico_annual_report_fx_table_method_closing') as string,
            methodAverage: this.i18next.t('aumico_annual_report_fx_table_method_average') as string,
            methodHistorical: this.i18next.t('aumico_annual_report_fx_table_method_historical') as string,
            checkBalance: this.i18next.t('aumico_annual_report_fx_proof_check_balance') as string,
            checkResult: this.i18next.t('aumico_annual_report_fx_proof_check_result') as string,
            reasonOpen: this.i18next.t('aumico_annual_report_fx_proof_reason_open') as string,
            reasonImbalance: this.i18next.t('aumico_annual_report_fx_proof_reason_imbalance') as string,
            reasonResultFunctional: this.i18next.t('aumico_annual_report_fx_proof_reason_result') as string,
            roundingAssign: this.i18next.t('aumico_annual_report_fx_rounding_assign') as string,
            roundingChange: this.i18next.t('aumico_annual_report_fx_rounding_change') as string,
            roundingTooLarge: this.i18next.t('aumico_annual_report_fx_rounding_too_large', {
                tolerance: FX_ROUNDING_TOLERANCE,
                currency: this.displayCurrencyName,
            }) as string,
            roundingAccountMissing: this.i18next.t('aumico_annual_report_fx_rounding_account_missing') as string,
            roundingCellHint: this.i18next.t('aumico_annual_report_fx_rounding_cell_hint') as string,
        };
        for (const year of ['currentYear', 'previousYear'] as const) {
            const labels = {
                [FxRateMethod.CLOSING]: this.methodLabel(FxRateMethod.CLOSING, year),
                [FxRateMethod.AVERAGE]: this.methodLabel(FxRateMethod.AVERAGE, year),
                [FxRateMethod.HISTORICAL]: this.methodLabel(FxRateMethod.HISTORICAL, year),
            };
            const previous = this.methodOptionLabels[year];
            this.methodOptionLabels[year] = labels;
            // The options array must keep its reference unless a label really changed: every new
            // reference triggers hamSimpleDropDown's ngOnChanges, which re-renders its last
            // WRITTEN value — and view-initiated selections never go through writeValue, so a
            // rebuilt array right after a click visually undoes the user's selection.
            const changed =
                this.methodOptions[year].length === 0 ||
                previous[FxRateMethod.CLOSING] !== labels[FxRateMethod.CLOSING] ||
                previous[FxRateMethod.AVERAGE] !== labels[FxRateMethod.AVERAGE];
            if (changed) {
                this.methodOptions[year] = [
                    { id: FxRateMethod.CLOSING, name: labels[FxRateMethod.CLOSING] },
                    { id: FxRateMethod.AVERAGE, name: labels[FxRateMethod.AVERAGE] },
                    { id: FxRateMethod.HISTORICAL, name: labels[FxRateMethod.HISTORICAL] },
                ];
            }
        }
    }

    /**
     * Get-or-create the dropdown control for one cell. Controls outlive re-decorations so the
     * dropdown keeps focus/state; only the value is re-synced (silently) from the stored method.
     */
    private controlFor(
        account: any,
        fliPath: string,
        year: 'currentYear' | 'previousYear',
        method: FxRateMethod,
    ): FormControl {
        const key = `${fliPath}|${this.accountKeyOf(account)}|${year}`;
        let control = this.methodControls.get(key);
        if (control == null) {
            control = new FormControl(method);
            control.valueChanges.subscribe((value: FxRateMethod | null) => {
                if (value != null) {
                    this.onMethodSelected(account, fliPath, year, value);
                }
            });
            this.methodControls.set(key, control);
        } else {
            // Always push, even when equal: setValue is the only path that refreshes the dropdown
            // directive's written value (view-initiated selections bypass writeValue), so the cell
            // keeps displaying correctly when the option labels change with new rates.
            control.setValue(method, { emitEvent: false });
        }

        const shouldDisable = year === 'previousYear' && this.previousYearLocked;
        if (shouldDisable && control.enabled) {
            control.disable({ emitEvent: false });
        } else if (!shouldDisable && control.disabled) {
            control.enable({ emitEvent: false });
        }

        return control;
    }

    private decorate(root: any, statement: 'balance' | 'income'): void {
        if (root == null) {
            return;
        }
        // The math is the helper's (one calculation everywhere); the FormControls are ours — the
        // helper is Angular-free, so the dropdown controls are attached in a second pass over the
        // freshly stamped decorations.
        FxTranslationHelper.decorate(root, statement, {
            fxTranslation: this.fxTranslation,
            yearResultFliNames: this.yearResultFliNames,
            equityFliNames: this.equityFliNames,
        });
        FxTranslationHelper.forEachAccount(root, (account) => {
            const fx = account.__fx as IFxDecoration | undefined;
            if (fx?.methodsCY?.[0] == null || fx?.methodsPY?.[0] == null) {
                return;
            }
            account.__fxCtrlCY = this.controlFor(account, account.__fxFliPath ?? '', 'currentYear', fx.methodsCY[0]);
            account.__fxCtrlPY = this.controlFor(account, account.__fxFliPath ?? '', 'previousYear', fx.methodsPY[0]);
        });
    }

    private historicalFor(account: any, fliPath: string): IFxAccountHistorical | undefined {
        return FxTranslationHelper.historicalFor(this.fxTranslation, account, fliPath);
    }

    /** Closed years for this account: what travelled forward, oldest first. */
    private archiveFor(account: any, fliPath: string): IFxHistoricalArchiveYear[] {
        return FxTranslationHelper.archiveFor(this.fxTranslation, account, fliPath);
    }

    private rateFor(year: 'currentYear' | 'previousYear', method: FxRateMethod): number | null {
        return FxTranslationHelper.rateFor(this.fxTranslation, year, method);
    }

    /** `accountNumber#name[ - shareholder][#uniqueAccountId]` — the ruled duplicate-safe key. */
    private accountKeyOf(account: any): string {
        return FxTranslationHelper.accountKeyOf(account);
    }

    private round2(value: number): number {
        return FxTranslationHelper.round2(value);
    }

    /**
     * The Translation Reserve is the balancing figure between translated Aktiven and translated
     * Passiven — it has no functional value and is never persisted.
     */
    private computeReserve(): void {
        const sections: any[] = (this.balanceSheet as any)?.sortedItems ?? [];
        const assets = sections[0]?.__fx as IFxDecoration | undefined;
        const liabilities = sections[1]?.__fx as IFxDecoration | undefined;
        // ONE figure, not two: the Bilanz row shows exactly what the Kontrolle tab proves. Computing
        // it twice would let the two drift apart precisely where it matters — the proof subtracts a
        // functional imbalance (03d §2.2), a plain `Aktiven − Passiven` would silently absorb it.
        this.reserveCY = this.proofCY?.reserve ?? null;
        this.reservePY = this.proofPY?.reserve ?? null;
        // A difference of two provisional sums is itself provisional.
        this.reserveProvisionalCY = assets?.provisionalCY === true || liabilities?.provisionalCY === true;
        this.reserveProvisionalPY = assets?.provisionalPY === true || liabilities?.provisionalPY === true;
    }

    /**
     * The year result, rendered as the income statement's closing total.
     *
     * The income statement's ROOT group IS the year result (`profit-of-the-year`); the sections are
     * its children. The recursive row walk starts at `root.sortedItems`, so the root's own figure
     * never appears in it — and as the statement's bottom line it belongs at the end anyway, not as
     * a header above everything.
     */
    private computeYearResult(): void {
        const root = this.incomeStatement as any;
        const fx = root?.__fx as IFxDecoration | undefined;
        // The template names it; only fall back to our own wording when it does not.
        this.resultLabel =
            root?.totalName ?? root?.title ?? (this.i18next.t('aumico_annual_report_fx_table_year_result') as string);
        this.resultUsdCY = fx?.usdCY ?? 0;
        this.resultUsdPY = fx?.usdPY ?? 0;
        this.resultChfCY = fx?.chfCY ?? null;
        this.resultChfPY = fx?.chfPY ?? null;
        this.resultRateCY = fx?.rateLabelCY ?? '';
        this.resultRatePY = fx?.rateLabelPY ?? '';
        this.resultProvisionalCY = fx?.provisionalCY === true;
        this.resultProvisionalPY = fx?.provisionalPY === true;
    }

    // #endregion

    // #region Kontrolle — the FX-difference proof (03d)

    /**
     * Builds both years' proofs and the control checks.
     *
     * The reserve exists because positions off the closing rate unbalance an otherwise balanced
     * sheet. Each line therefore carries its CONTRIBUTION — `+d` on the asset side, `−d` on the
     * other — so the column adds up to the reserve with a plain sum instead of a negated one, which
     * would silently invert asset positions (03d §2).
     */
    private buildProof(): void {
        this.proofCY = this.buildProofYear('currentYear');
        this.proofPY = this.buildProofYear('previousYear');
        this.checks = this.buildChecks();
        this.functionalImbalances = [this.proofCY, this.proofPY]
            .filter((proof): proof is IFxProofYear => proof != null && proof.functionalDelta !== 0)
            .map((proof) => ({ header: proof.header, delta: proof.functionalDelta }));
    }

    private buildProofYear(year: 'currentYear' | 'previousYear'): IFxProofYear | null {
        // The math is the helper's; this component only adds the display strings (header, method
        // labels) the Kontrolle template binds.
        const data = FxTranslationHelper.proofYear(this.balanceSheet, year, this.fxTranslation);
        if (data == null) {
            return null;
        }

        return {
            header: year === 'currentYear' ? this.headerBalanceCY : this.headerBalancePY,
            closingRate: data.closingRate,
            rows: data.rows.map((row) => this.labelProofRow(row)),
            sumContribution: data.sumContribution,
            rounding: data.rounding,
            reserve: data.reserve,
            functionalDelta: data.functionalDelta,
            provisional: data.provisional,
        };
    }

    /** Turns the helper's label-free proof row into the template's labelled one. */
    private labelProofRow(row: IFxProofRowData): IFxProofRow {
        const distinct = row.methods;

        return {
            label: row.label,
            methodLabel: distinct.length > 1 ? this.labels.mixed : this.methodName(distinct[0]),
            mixedDetail: distinct.length > 1 ? distinct.map((m) => this.methodName(m)).join(' · ') : '',
            rate: row.rate,
            display: row.display,
            displayAtClosing: row.displayAtClosing,
            contribution: row.contribution,
            functional: row.functional,
            provisional: row.provisional,
        };
    }

    // proofRowsOf lives in FxTranslationHelper (REV-21512) — labelProofRow above adds the strings.

    private buildChecks(): IFxCheck[] {
        // Without a proof there is nothing to check. Returning checks anyway would report GREEN on a
        // report whose rates are not entered yet: every CHF figure is then 0, and 0 = 0 passes.
        if (this.proofCY == null || this.proofPY == null) {
            return [];
        }
        const sections: any[] = (this.balanceSheet as any)?.sortedItems ?? [];
        const checks: IFxCheck[] = [];
        for (const year of ['currentYear', 'previousYear'] as const) {
            const isCY = year === 'currentYear';
            const proof = (isCY ? this.proofCY : this.proofPY) as IFxProofYear;
            const assetsChf = this.round2((isCY ? sections[0]?.__fx?.chfCY : sections[0]?.__fx?.chfPY) ?? 0);
            const liabilitiesChf = (isCY ? sections[1]?.__fx?.chfCY : sections[1]?.__fx?.chfPY) ?? 0;
            const withReserve = this.round2(liabilitiesChf + proof.reserve);
            const openCount = this.openPositionCount(year);
            checks.push({
                // The currency is the whole point: functionally the sheet balances (or the reason
                // below says it does not) — what these checks prove is that it still does TRANSLATED.
                label: `${this.labels.checkBalance} ${isCY ? this.headerBalanceCY : this.headerBalancePY} (${
                    this.displayCurrencyName
                })`,
                ok: openCount === 0 && proof.functionalDelta === 0 && assetsChf === withReserve,
                left: assetsChf,
                right: withReserve,
                difference: this.round2(assetsChf - withReserve),
                // A red row above the panels must say WHICH cause, since the proof is not in view —
                // and BOTH when both apply, or fixing one would leave the row stubbornly red.
                reason: [
                    openCount > 0 ? `${this.labels.reasonOpen} — ${openCount}` : '',
                    proof.functionalDelta !== 0
                        ? `${this.labels.reasonImbalance} ${this.functionalCurrencyName} ${proof.functionalDelta.toFixed(2)}`
                        : '',
                ]
                    .filter((part) => part !== '')
                    .join(' · '),
            });
        }

        // The year result, one card per year: each account rounds on its own, the balance sheet rounds
        // the year result once — so the two differ by cents until the user books that difference
        // onto an account (the helper does the booking; these cards only report and offer it).
        const bsResult = this.yearResultFli();
        const rounding = FxTranslationHelper.roundingOf(this.incomeStatement);
        for (const year of ['currentYear', 'previousYear'] as const) {
            const isCY = year === 'currentYear';
            const bsFx = bsResult?.__fx as IFxDecoration | undefined;
            const bsUsd = bsResult == null ? 0 : isCY ? this.finalCY(bsResult) : this.finalPY(bsResult);
            const bsChf = this.round2((isCY ? bsFx?.chfCY : bsFx?.chfPY) ?? 0);
            const erChf = this.round2((isCY ? this.resultChfCY : this.resultChfPY) ?? 0);
            const gap = this.round2(bsChf - erChf);
            const functionalMatches = this.round2(bsUsd) === this.round2(isCY ? this.resultUsdCY : this.resultUsdPY);
            const state = rounding?.[year];
            const ok = functionalMatches && gap === 0;
            const offerable = state != null && (state.bookable || state.account != null);
            checks.push({
                label: `${this.labels.checkResult} ${isCY ? this.headerBalanceCY : this.headerBalancePY} (${
                    this.displayCurrencyName
                })`,
                ok,
                warning: !ok && state?.bookable === true,
                left: bsChf,
                right: erChf,
                difference: gap,
                reason: functionalMatches ? '' : this.labels.reasonResultFunctional,
                roundingYear: offerable ? year : undefined,
                note: this.roundingNote(state),
                action: offerable
                    ? state?.booked !== 0
                        ? this.labels.roundingChange
                        : this.labels.roundingAssign
                    : undefined,
            });
        }

        return checks;
    }

    /** The balance sheet's year-result FLI — the figure check 3 compares against. */
    private yearResultFli(): any {
        return FxTranslationHelper.yearResultFli(this.balanceSheet, this.yearResultFliNames);
    }

    private openPositionCount(year: 'currentYear' | 'previousYear'): number {
        return FxTranslationHelper.openPositionCount(this.balanceSheet, year);
    }

    private methodName(method: FxRateMethod | undefined): string {
        if (method === FxRateMethod.HISTORICAL) {
            return this.labels.methodHistorical;
        }

        return method === FxRateMethod.AVERAGE ? this.labels.methodAverage : this.labels.methodClosing;
    }

    // #endregion

    // #region template API

    /**
     * The account number to show — EMPTY for a Grouping & Masking group and for its split
     * counterpart. Those carry a synthetic `accountNumber` (the group id, resp. `<groupId>_
     * counterpart`) that is an internal identity, never something the user typed; `groupId` is the
     * marker both of them carry. Printing suppresses the same ids via
     * `MaskingHelper.getAccountDisplayName`, which cannot be reused here: this table renders number
     * and name as two cells, and its names are already masked by `buildViewModels`.
     */
    /** Where the rounding difference went — or why it could not go anywhere. */
    private roundingNote(state: IFxRoundingYear | undefined): string | undefined {
        if (state == null || state.difference === 0) {
            return undefined;
        }
        if (!state.bookable) {
            return this.labels.roundingTooLarge;
        }
        if (state.account != null && !state.accountFound) {
            return this.labels.roundingAccountMissing;
        }
        if (state.booked !== 0 && state.account != null) {
            return this.i18next.t('aumico_annual_report_fx_rounding_booked', {
                amount: `${state.booked > 0 ? '+' : ''}${state.booked.toFixed(2)} ${this.displayCurrencyName}`,
                account: this.roundingAccountLabel(state.account),
            }) as string;
        }

        return undefined;
    }

    private roundingAccountLabel(ref: IFxAccountRef): string {
        const match = FxTranslationHelper.roundingCandidates(this.incomeStatement, 'currentYear')
            .concat(FxTranslationHelper.roundingCandidates(this.incomeStatement, 'previousYear'))
            .find((candidate) => candidate.ref.fliPath === ref.fliPath && candidate.ref.accountKey === ref.accountKey);

        return match == null
            ? ref.accountKey
            : `${this.accountNumberOf(match.account)} ${match.account.name ?? ''}`.trim();
    }

    openRoundingDialog(check: IFxCheck): void {
        const year = check.roundingYear;
        const rounding = year == null ? undefined : FxTranslationHelper.roundingOf(this.incomeStatement)?.[year];
        if (year == null || rounding == null) {
            return;
        }
        const options = FxTranslationHelper.roundingCandidates(this.incomeStatement, year).map((candidate) => ({
            id: this.roundingOptionId(candidate.ref),
            name: `${this.accountNumberOf(candidate.account)} ${candidate.account.name ?? ''}`.trim(),
        }));
        const locked = year === 'previousYear' && this.previousYearLocked;
        const control = new FormControl(rounding.account != null ? this.roundingOptionId(rounding.account) : null);
        if (locked) {
            control.disable();
        }
        // The house dropdown writes its value through `registerOnChange` from a Renderer2 listener,
        // which marks nothing dirty under OnPush — the «Zuordnen» button would stay grey until the
        // next click anywhere (same fix as assign-accounts.component.ts).
        control.valueChanges.subscribe(() => this.cdr.markForCheck());
        this.roundingDialog = {
            year,
            header: year === 'currentYear' ? this.headerBalanceCY : this.headerBalancePY,
            rounding,
            options,
            control,
            locked,
        };
    }

    onRoundingAssign(): void {
        const dialog = this.roundingDialog;
        const selected = dialog?.control.value as string | null | undefined;
        if (dialog == null || dialog.locked || selected == null) {
            return;
        }
        const [fliPath, accountKey] = JSON.parse(selected) as [string, string];
        this.roundingDialog = null;
        this.roundingAccountChange.emit({ year: dialog.year, account: { fliPath, accountKey } });
    }

    onRoundingRemove(): void {
        const dialog = this.roundingDialog;
        if (dialog == null || dialog.locked) {
            return;
        }
        this.roundingDialog = null;
        this.roundingAccountChange.emit({ year: dialog.year, account: null });
    }

    onRoundingCancel(): void {
        this.roundingDialog = null;
    }

    /** One string per account for the dropdown — both key levels, unambiguous. */
    private roundingOptionId(ref: IFxAccountRef): string {
        return JSON.stringify([ref.fliPath, ref.accountKey]);
    }

    accountNumberOf(account: { accountNumber?: string; groupId?: string }): string {
        return account?.groupId != null ? '' : account?.accountNumber ?? '';
    }

    onTabClick(item: NavigationItem): void {
        const index = this.navigationItems.findIndex((nav) => nav.name === item.name);
        if (index !== -1) {
            this.navigationItems.forEach((nav, i) => (nav.active = i === index));
            this.selectedTab = index;
        }
    }

    onMethodSelected(account: any, fliPath: string, year: 'currentYear' | 'previousYear', value: string): void {
        this.methodChange.emit({
            fliPath,
            accountKey: this.accountKeyOf(account),
            year,
            method: value as FxRateMethod,
        });
    }

    private methodLabel(method: FxRateMethod | undefined, year: 'currentYear' | 'previousYear'): string {
        if (method === FxRateMethod.HISTORICAL) {
            // Per-account rate / movements — no single rate to prefix here.
            return this.i18next.t('aumico_annual_report_fx_table_method_historical') as string;
        }
        const rate = method != null ? this.rateFor(year, method) : null;
        const name =
            method === FxRateMethod.AVERAGE
                ? (this.i18next.t('aumico_annual_report_fx_table_method_average') as string)
                : (this.i18next.t('aumico_annual_report_fx_table_method_closing') as string);

        return rate != null ? `${NumberUtils.formatFxRate(rate)} · ${name}` : name;
    }

    toggleCollapse(path: string): void {
        this.accordionToggles.set(path, !this.isCollapsed(path));
    }

    isCollapsed(path: string): boolean {
        // Default: groups start open, FLIs (and everything below — sub-FLIs, davon blocks)
        // start collapsed. Paths at or below a FLI always carry the __fli__ marker.
        return this.accordionToggles.get(path) ?? path.includes('__fli__');
    }

    /** Ask the parent to open the historical editor for this account-year. */
    onEditHistorical(account: any, year: 'currentYear' | 'previousYear'): void {
        const fliPath = account.__fxFliPath ?? '';
        this.historicalEdit.emit({
            fliPath,
            accountKey: this.accountKeyOf(account),
            year,
            accountLabel: `${this.accountNumberOf(account)} ${account.name ?? ''}`.trim(),
            functionalOpening: this.finalPY(account),
            functionalClosing: this.finalCY(account),
            previousYearHistorical: account?.__fx?.historicalPY === true,
            archive: this.archiveFor(account, fliPath),
            previousYearRate: account?.__fx?.ratePY ?? null,
            previousYearLocked: this.previousYearLocked,
            historical: this.historicalFor(account, fliPath),
        });
    }

    // #endregion
}
